import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Switch, Spinner, Typography, ConfirmationModal, useOverlayContext } from 'seagull';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { timeModel } from '~/models';
import { useBillingObservable } from '~/providers/BillingObservableProvider';
import styles from '../BillingControl.module.scss';
function PauseBillingConfirmationModal(props) {
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: switchTexts } = useLocaleKeys('pages.agreementMinisite.billingHub.ongoingServiceSwitch');
    const { serviceName, dismissModal } = props;
    const title = switchTexts.pauseAutomaticBilling();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'ongoing_service_switch_1_confirm'
            },
            secondaryButton: {
                name: 'ongoing_service_switch_1_cancel'
            }
        },
        title: title,
        testId: "pause-confirmation-modal",
        textAlignCenter: false,
        width: "701",
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        switchTexts.pauseBodyPrefix(),
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "pause-confirmation-modal-service-name",
                            as: "span",
                            weight: "bold",
                            children: serviceName
                        }),
                        switchTexts.pauseBodySuffix()
                    ]
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    children: switchTexts.pauseNotBilled()
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    style: {
                        marginTop: '1rem'
                    },
                    children: switchTexts.pauseWarning()
                })
            ]
        }),
        primaryButtonLabel: title,
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onPrimaryButtonClick: ()=>dismissModal(true),
        onSecondaryButtonClick: ()=>dismissModal(false)
    });
}
function ResumeBillingConfirmationModal(props) {
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: switchTexts } = useLocaleKeys('pages.agreementMinisite.billingHub.ongoingServiceSwitch');
    const { serviceName, nextBillingDate, dismissModal } = props;
    const title = switchTexts.resumeAutomaticBilling();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'ongoing_service_switch_2_confirm'
            },
            secondaryButton: {
                name: 'ongoing_service_switch_2_cancel'
            }
        },
        title: title,
        testId: "resume-confirmation-modal",
        textAlignCenter: false,
        width: "701",
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        switchTexts.resumeBodyPrefix(),
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "resume-confirmation-modal-service-name",
                            as: "span",
                            weight: "bold",
                            children: serviceName
                        }),
                        switchTexts.resumeBodySuffix()
                    ]
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    style: {
                        marginTop: '1rem'
                    },
                    children: [
                        switchTexts.resumeNextInvoicePrefix(),
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "resume-confirmation-modal-next-billing-date",
                            as: "span",
                            weight: "bold",
                            children: timeModel.formatDate(nextBillingDate)
                        }),
                        switchTexts.resumeNextInvoiceSuffix()
                    ]
                })
            ]
        }),
        primaryButtonLabel: title,
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onPrimaryButtonClick: ()=>dismissModal(true),
        onSecondaryButtonClick: ()=>dismissModal(false)
    });
}
export default function OngoingServiceSwitch(props) {
    const { agreementId, serviceId, serviceName, isPaused, isDisabled, tooltipTitle, testId } = props;
    const { notifyToRefresh } = useBillingObservable();
    const [isResumed, setIsResumed] = useState(!isPaused);
    const [loading, setLoading] = useState(false);
    const overlayContext = useOverlayContext();
    const { api } = useStoreActions();
    const { t: switchTexts } = useLocaleKeys('pages.agreementMinisite.billingHub.ongoingServiceSwitch');
    async function onChange(e) {
        const { checked, value } = e.target;
        if (!checked /* pause service */ ) {
            const confirmed = await overlayContext.showModal({
                biName: 'pause_billing_confirmation_modal',
                content: (props)=>/*#__PURE__*/ _jsx(PauseBillingConfirmationModal, _object_spread_props(_object_spread({}, props), {
                        serviceName: serviceName
                    }))
            });
            if (!confirmed) return;
        } else {
            const nextBillingDate = await api.service.nextBillingDate(agreementId, serviceId);
            const confirmed = await overlayContext.showModal({
                biName: 'resume_billing_confirmation_modal',
                content: (props)=>/*#__PURE__*/ _jsx(ResumeBillingConfirmationModal, _object_spread_props(_object_spread({}, props), {
                        serviceName: serviceName,
                        nextBillingDate: nextBillingDate
                    }))
            });
            if (!confirmed) return;
        }
        try {
            setLoading(true);
            await api.service.togglePauseResumeBilling(agreementId, value, !checked);
            notifyToRefresh();
            setIsResumed(checked);
        } catch (error) {
            const message = checked ? switchTexts.failedToResumeService() : switchTexts.failedToPauseService();
            overlayContext.showErrorToast(message, 'pause-service-error-toast');
        } finally{
            setLoading(false);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.switch,
        children: [
            loading && /*#__PURE__*/ _jsx(Spinner, {
                size: 24
            }),
            /*#__PURE__*/ _jsx(Switch, {
                bi: {
                    name: 'resume_ongoing_service'
                },
                disabled: loading || isDisabled,
                value: serviceId,
                checked: isResumed,
                testId: testId,
                tooltip: tooltipTitle ? {
                    placement: 'top',
                    title: /*#__PURE__*/ _jsx("div", {
                        "data-testid": testId && "".concat(testId, "-tooltip"),
                        children: tooltipTitle
                    })
                } : undefined,
                onChange: onChange
            })
        ]
    });
}
